/* eslint-disable no-prototype-builtins */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getEmoji } from '@nile/mangifera/src/Pebbles/WidgetEmoji';
import {
  AppContext,
  AutoCompleteSearchInput,
  Emoji,
  Loader,
  getHighlightedText,
  getTranslatedText,
  isAuthorised,
  useActivityTracker,
  withErrorBoundary
} from '@nile/components';
import { AccountClient } from '@nile/graphql';
import { useLazyQuery } from '@apollo/client';
import { GET_CHARTS_LIST } from '@nile/reports/src/modules/charts/graphql';
import { SVGIcon } from '@superops/super-icons';
import { ResourceItem, TextStyle } from '@superops/ui-design-system';
import { CHART_ICON } from '@nile/reports/src/modules/charts/utils/chart-transformation';
import { isEmpty } from 'lodash';

function GroupedLibraryMenu(props) {
  const {
    allMentions,
    allViews,
    handleClick,
    filterExpression,
    debouncedFilter,
    layout,
    isInClientDetailsPage
  } = props;

  const intl = useIntl();

  const appContext = useContext(AppContext);
  const { user } = appContext;
  const reference = user?.role?.roleFeatureList || [];

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [availableGroups, setAvailableGroups] = useState([]);

  const allViewsRef = useRef(allViews);
  allViewsRef.current = allViews;

  const filterAndSetItems = (prop) => {
    const {
      items = [],
      refresh = false,
      widgetType = selectedGroup?.widgetType
    } = prop;
    const allItem = refresh ? selectedItems : items;
    let availableItems = [];
    if (widgetType === 'LISTVIEW') {
      availableItems = allItem.filter((el) => {
        return !layout.some(
          (w) => w.widget.id === (el.listView?.id || el.widgetId)
        );
      });
    }
    if (widgetType === 'REPORT') {
      availableItems = allItem.filter((el) => {
        return !layout.some(
          (w) =>
            w[isInClientDetailsPage ? 'cardData' : 'widget'].chartId ===
            el.chartId
        );
      });
    }
    if (widgetType === 'MENTION') {
      availableItems = allItem.filter((el) => {
        return !layout.some((w) => w.widget.type === el.widgetType);
      });
    }
    setAllItems(availableItems);
    setSelectedItems(availableItems);
  };

  const [getWidgets, { loading: widgetsLoading }] = useLazyQuery(
    GET_CHARTS_LIST,
    {
      fetchPolicy: 'cache-and-network',
      client: AccountClient,
      onCompleted: ({ getCharts }) => {
        filterAndSetItems({
          items: getCharts?.charts || [],
          widgetType: 'REPORT'
        });
      }
    }
  );

  const setGroupAndItems = (grp) => {
    setSelectedGroup({
      name: grp?.label ?? grp?.name,
      widgetType: grp.hasOwnProperty('type')
        ? 'REPORT'
        : grp?.items?.[0]?.widgetType
    });
    if (grp.hasOwnProperty('type') && grp?.type === 'WIDGET') {
      getWidgets({
        variables: {
          listInfo: {
            condition: {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'systemChart',
                  operator: 'is',
                  value: grp?.listInfo?.systemChart
                }
              ]
            },
            view: { id: grp?.listInfo?.id }
          }
        }
      });
    } else {
      filterAndSetItems({
        items: grp?.items,
        widgetType: grp?.items?.[0]?.widgetType
      });
    }
  };

  const constructGroup = () => {
    if (isInClientDetailsPage) {
      const group = [];
      if (isAuthorised(reference, 'report.view')) {
        group.push(
          {
            name: 'admin.home.widgetLibrary.default.reports',
            isNested: true,
            items: [
              {
                name: 'reports.common.allWidgets',
                label: 'DEFAULT_ALL_WIDGET',
                type: 'WIDGET',
                listInfo: { systemChart: true, id: 2001 }
              }
            ]
          },
          {
            name: 'admin.home.widgetLibrary.custom.reports',
            isNested: true,
            items: [
              {
                name: 'reports.common.allWidgets',
                type: 'WIDGET',
                label: 'CUSTOM_ALL_WIDGET',
                listInfo: { systemChart: false, id: 2001 }
              },
              {
                name: 'report.chart.listView.myCharts',
                type: 'WIDGET',
                label: 'CUSTOM_MY_WIDGET',
                listInfo: { systemChart: false, id: 2002 }
              }
            ]
          }
        );
      }

      setSelectedGroup({
        name: group[0].items[0].label,
        widgetType: 'WIDGET'
      });
      filterAndSetItems({
        items: group[0].items,
        widgetType: 'WIDGET'
      });
      setGroupAndItems(group[0].items[0]);
      setAvailableGroups(group);
      return;
    }
    const group = [
      ...allViewsRef.current
        ?.filter((el) => !isEmpty(el?.items))
        .filter((el) =>
          !isEmpty(el?.permission?.trim())
            ? isAuthorised(reference, el?.permission)
            : true
        )
    ];
    group.push({
      name: 'admin.home.widgetlibrary.mentions.all',
      items: allMentions
    });
    if (isAuthorised(reference, 'report.view')) {
      group.push(
        {
          name: 'admin.home.widgetLibrary.default.reports',
          isNested: true,
          items: [
            {
              name: 'reports.common.allWidgets',
              label: 'DEFAULT_ALL_WIDGET',
              type: 'WIDGET',
              listInfo: { systemChart: true, id: 2001 }
            }
          ]
        },
        {
          name: 'admin.home.widgetLibrary.custom.reports',
          isNested: true,
          items: [
            {
              name: 'reports.common.allWidgets',
              type: 'WIDGET',
              label: 'CUSTOM_ALL_WIDGET',
              listInfo: { systemChart: false, id: 2001 }
            },
            {
              name: 'report.chart.listView.myCharts',
              type: 'WIDGET',
              label: 'CUSTOM_MY_WIDGET',
              listInfo: { systemChart: false, id: 2002 }
            }
          ]
        }
      );
    }
    setSelectedGroup({
      name: group[0].name || null,
      widgetType: 'LISTVIEW'
    });
    filterAndSetItems({
      items: group[0].items,
      widgetType: 'LISTVIEW'
    });
    setAvailableGroups(group);
  };

  const getNameAndEmoji = (mod) => {
    switch (mod?.widgetType) {
      case 'MENTION':
        return {
          name: getTranslatedText(intl, 'admin.home.widgetlibrary.mentions.my'),
          emoji: getEmoji({ module: mod?.widgetType })
        };
      case 'LISTVIEW':
        return { name: mod?.listView?.name, emoji: getEmoji(mod?.listView) };
      default:
        if (mod.hasOwnProperty('systemChart')) {
          return {
            name: mod?.name,
            emoji: (
              <div className="flex actr jctr mr-10">
                <SVGIcon
                  name={
                    mod?.defaultChartType
                      ? CHART_ICON[mod?.defaultChartType]
                      : 'NumberChart'
                  }
                  size="lg"
                />
              </div>
            )
          };
        }
        return {
          name: mod?.name,
          emoji: (
            <div className="mr12 emoji">
              <Emoji symbol="🌟" label="my_mentions" />
            </div>
          )
        };
    }
  };

  const getModuleEmoji = (mod) => {
    if (mod?.hasOwnProperty('type')) {
      return getEmoji({ module: mod?.type });
    }
    if (mod?.items?.[0]?.listView?.module) {
      return getEmoji({ module: mod?.items?.[0]?.listView?.module });
    }
    if (mod?.items?.[0]?.widgetType === 'MENTION') {
      return getEmoji({ module: mod?.items?.[0]?.widgetType });
    }
    return undefined;
  };

  const addToHomeLayout = (wgt) => {
    if (wgt?.widgetType === 'MENTION') {
      handleClick({ id: `w-${wgt.widgetId}`, type: wgt.widgetType });
    }
    if (wgt?.widgetType === 'LISTVIEW') {
      handleClick({ widgetType: wgt.widgetType, ...wgt?.listView });
    }
    if (wgt.hasOwnProperty('systemChart')) {
      handleClick({ type: 'ReportWidget', ...wgt });
    }
  };

  const listEl = (el, nested = false) => {
    const isActiveSection = selectedGroup?.name === (el?.label ?? el?.name);
    return (
      <>
        <ResourceItem
          verticalAlignment="center"
          gutter="md"
          onClick={() => !el?.isNested && setGroupAndItems(el)}
          isSelected={isActiveSection}
        >
          <div className="fx actr">
            {!el?.isNested && getModuleEmoji(el)}
            {el?.isNested ? (
              <div className="uppercase">
                <TextStyle weight="SemiBold" variation="subdued">
                  <FormattedMessage id={el?.name} />
                </TextStyle>
              </div>
            ) : (
              <FormattedMessage id={el?.name} />
            )}
          </div>
        </ResourceItem>
        {el?.isNested && el?.items?.map((nestedEl) => listEl(nestedEl, true))}
      </>
    );
  };

  useEffect(() => {
    filterAndSetItems({ refresh: true });
  }, [layout]);

  useEffect(() => {
    if (filterExpression) {
      const searchItems = selectedItems.filter((view) => {
        const { name } = getNameAndEmoji(view);
        return name.toLowerCase().includes(filterExpression.toLowerCase());
      });
      setSelectedItems(searchItems);
    } else {
      setSelectedItems(allItems);
    }
  }, [filterExpression, allItems]);

  useEffect(() => {
    constructGroup();
  }, []);

  return (
    <div className="fh no-scroll">
      <div className="fx fw fh">
        <div className="w40 border-right pr10 scroll">
          {availableGroups.map((el) => listEl(el))}
        </div>
        <div className="w60 pl-10 pr10 scroll">
          <div className="position-sticky m-0 bg-white z99">
            <AutoCompleteSearchInput
              debouncedFilter={debouncedFilter}
              noMargin
            />
          </div>

          {widgetsLoading ? (
            <Loader />
          ) : (
            <>
              {!selectedItems?.length ? (
                <div className="fx fh jctr actr">
                  <div>
                    <div className="fx actr jctr">
                      <SVGIcon name="NotFound" />
                    </div>
                    <div className="fx actr jctr">
                      <TextStyle weight="Medium">
                        {getTranslatedText(
                          intl,
                          'admin.home.widgetLibrary.noWidget'
                        )}
                      </TextStyle>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="scroll">
                  {selectedItems?.map((itm) => {
                    const { name, emoji } = getNameAndEmoji(itm);
                    return (
                      <ResourceItem
                        verticalAlignment="center"
                        gutter="md"
                        onClick={() => {
                          useActivityTracker({
                            event: 'HOME_WIDGET',
                            data: {
                              widgetName: name,
                              widgetType: selectedGroup?.widgetType
                            }
                          });
                          addToHomeLayout(itm);
                        }}
                      >
                        <div className="flex actr">
                          {emoji}
                          {filterExpression
                            ? getHighlightedText(name, filterExpression)
                            : name}
                        </div>
                      </ResourceItem>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default withErrorBoundary(GroupedLibraryMenu);
